import { motion } from "motion/react";
import { SEO } from "../components/seo";
import { Code2, Zap, Users, Target, Award, Heart, Server, Database, Layout, Smartphone, Globe, Shield } from "lucide-react";

export function About() {
  const values = [
    {
      icon: Code2,
      title: "Clean Code",
      description: "Writing maintainable, scalable code that stands the test of time.",
    },
    {
      icon: Zap,
      title: "Performance First",
      description: "Optimizing every detail for lightning-fast user experiences.",
    },
    {
      icon: Users,
      title: "User-Centric",
      description: "Designing solutions that solve real problems for real people.",
    },
    {
      icon: Target,
      title: "Goal-Oriented",
      description: "Focusing on business outcomes and measurable results.",
    },
    {
      icon: Award,
      title: "Excellence",
      description: "Delivering work that exceeds expectations every single time.",
    },
    {
      icon: Heart,
      title: "Passion",
      description: "Genuinely loving what I do and bringing energy to every project.",
    },
  ];

  const expertise = [
    { icon: Layout, title: "Frontend Development", skills: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Framer Motion"] },
    { icon: Server, title: "Backend Systems", skills: ["Node.js", "PHP", "Express", "Hono", "REST APIs"] },
    { icon: Database, title: "Database Design", skills: ["PostgreSQL", "MySQL", "Prisma", "Supabase", "Redis"] },
    { icon: Globe, title: "Cloud & DevOps", skills: ["Vercel", "DigitalOcean", "Docker", "CI/CD", "DNS Management"] },
    { icon: Smartphone, title: "Mobile Solutions", skills: ["Capacitor", "Responsive Web", "PWA", "Mobile-First Design"] },
    { icon: Shield, title: "Security & Auth", skills: ["JWT", "OAuth", "RLS", "Data Encryption", "SSL"] },
  ];

  const timeline = [
    {
      year: "2020",
      title: "Started Freelancing",
      description: "Began working with clients on web development and design projects.",
    },
    {
      year: "2021",
      title: "Full-Stack Focus",
      description: "Expanded into backend development with Node.js and database design.",
    },
    {
      year: "2022",
      title: "Business Solutions",
      description: "Specialized in CRM systems, automation, and enterprise applications.",
    },
    {
      year: "2023",
      title: "Modern Stack",
      description: "Mastered React, TypeScript, and cloud infrastructure.",
    },
    {
      year: "2024-Present",
      title: "Full-Service Studio",
      description: "Offering complete solutions from design to deployment.",
    },
  ];

  return (
    <>
      <SEO 
        title="About - Developer & Designer" 
        description="Learn about Ergys Onuzi's journey as a full-stack developer and designer. Discover my values, expertise, and approach to building exceptional digital solutions."
        canonical="/about"
      />

      <div className="min-h-screen bg-black text-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-32 pb-20 px-6">
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-500 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-fuchsia-500 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>

          <div className="max-w-4xl mx-auto relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
                About Me
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
                Crafting digital experiences that blend beautiful design with powerful functionality
              </p>
            </motion.div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-violet-950/20 via-fuchsia-950/20 to-cyan-950/20 rounded-3xl p-8 md:p-12 border border-violet-500/20 backdrop-blur-sm"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                My Story
              </h2>
              <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  I'm Ergys Onuzi, a full-stack developer and designer with a passion for creating 
                  exceptional digital experiences. My journey in tech started with a curiosity about 
                  how things work and evolved into a full-blown love affair with code and design.
                </p>
                <p>
                  Over the years, I've had the privilege of working with diverse clients—from startups 
                  finding their footing to established businesses optimizing their operations. Each project 
                  has taught me something new and reinforced my belief that the best solutions come from 
                  understanding both the technical possibilities and the human needs.
                </p>
                <p>
                  I specialize in building custom business solutions that actually solve problems. Whether 
                  it's a CRM system that streamlines operations, an e-commerce platform that drives sales, 
                  or a web application that delights users, I bring the same level of dedication and 
                  expertise to every project.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Expertise Section */}
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                Technical Expertise
              </h2>
              <p className="text-gray-400 text-lg">Specialized skills for modern digital solutions</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {expertise.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="p-8 bg-gradient-to-br from-violet-950/20 via-fuchsia-950/20 to-cyan-950/20 rounded-2xl border border-violet-500/20 hover:border-violet-400/40 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center mb-6">
                    <item.icon className="w-6 h-6 text-violet-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-white">{item.title}</h3>
                  <div className="flex flex-wrap gap-2">
                    {item.skills.map(skill => (
                      <span key={skill} className="px-3 py-1 bg-black/40 border border-violet-500/10 rounded-full text-xs text-gray-400">
                        {skill}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent via-violet-950/10 to-transparent">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
                What I Stand For
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                The principles that guide every project I work on
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group bg-gradient-to-br from-violet-950/40 via-fuchsia-950/40 to-cyan-950/40 rounded-2xl p-6 border border-violet-500/20 hover:border-violet-400/50 transition-all duration-300 hover:scale-105"
                >
                  <div className="mb-4 relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                      <value.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute inset-0 w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 blur-xl opacity-0 group-hover:opacity-50 transition-opacity" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white">{value.title}</h3>
                  <p className="text-gray-400">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent">
                My Journey
              </h2>
              <p className="text-gray-400 text-lg">
                The path that led me here
              </p>
            </motion.div>

            <div className="space-y-8">
              {timeline.map((item, index) => (
                <motion.div
                  key={item.year}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative pl-8 border-l-2 border-violet-500/30"
                >
                  <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
                  <div className="bg-gradient-to-br from-violet-950/20 via-fuchsia-950/20 to-cyan-950/20 rounded-xl p-6 border border-violet-500/20 hover:border-violet-400/40 transition-all duration-300">
                    <div className="text-sm text-violet-400 font-semibold mb-2">{item.year}</div>
                    <h3 className="text-xl font-bold mb-2 text-white">{item.title}</h3>
                    <p className="text-gray-400">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-600 via-fuchsia-600 to-cyan-600 p-12 text-center"
            >
              <div className="absolute inset-0 bg-black/20" />
              <div className="relative">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                  Let's Build Something Amazing
                </h2>
                <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                  Ready to bring your ideas to life? I'd love to hear about your project.
                </p>
                <a
                  href="/contact"
                  className="inline-block px-8 py-4 bg-white text-violet-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-300 shadow-xl"
                >
                  Get In Touch
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}